import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { IBatchData } from '../../../../../school/student-level-dashboard/student-level-dash.component';
import { IPageHeaderMeta } from '../../../../components/header/page-header/page-header-meta.interface';
import { IGroupData, IListConfig, IRowData } from '../../../../models/list-models';
import { Direction, TSortDirections } from '../../../../services/list-services/sort-and-filter.service';
import { IDropdownOption } from '../../../../typings/interfaces/design-library.interface';

export interface IClusterUserPermissions {
  cluster_edit_all: boolean;
  cluster_view_all: boolean;
  edit_all?: string[];
  view_all?: string[];
  no_access?: string[];
}

@Component({
  selector: 'user-portfolio-modal',
  template: require('./cluster-user-portfolio-modal.component.html'),
  styles: [require('./cluster-user-portfolio-modal.component.scss')],
})
export class UserPortfolioModalComponent implements OnInit {
  // HEADER PART
  @Input() user: {
    name: string;
    id: string | null;
  };
  @Input() iconName: string;
  @Input() btnName: string;
  public pageHeaderMeta: IPageHeaderMeta;

  // BATCH EDIT TOOL BAR
  public batchEditOptions: IDropdownOption[] = [
    {
      key: 'no_access',
      human: 'Set to No access',
    },
    {
      key: 'view_all',
      human: 'Set to View only',
    },
    {
      key: 'edit_all',
      human: 'Set to Can edit',
    },
  ];

  private permissionsChanges: IClusterUserPermissions = {
    cluster_edit_all: false,
    cluster_view_all: false,
    edit_all: [],
    view_all: [],
    no_access: [],
  };

  private dropdownHuman = {
    no_access: 'No access',
    view_all: 'View only',
    edit_all: 'Can edit',
  };

  // needed by list-container
  @Input() groupingData$: BehaviorSubject<null | IGroupData[]>;
  public sortKey$: BehaviorSubject<string>;
  public sortDirection$: BehaviorSubject<TSortDirections>;
  public schoolFilter: FormControl = new FormControl('');
  public batchActionsMode$: Observable<boolean>;
  public batchActionsSelectedIds$: BehaviorSubject<string[]>;
  public batchActions: any;
  public columns: any[];
  public columnIndexMap: { [key: string]: number };
  public listConfig: IListConfig;
  public dynamicComponentTrigger: boolean;

  // OUTPUT EVENT
  @Output() clickedPrimaryBtn = new EventEmitter();
  @Output() clickedSeconBtn = new EventEmitter();

  @ViewChild('modalContainer', { static: false }) modalContainer: ElementRef;

  ngOnInit(): void {
    this.pageHeaderMeta = {
      textboxPlaceholder: 'Find a school...',
      icon: {
        name: this.iconName,
        tooltip: null,
        handler: this.onClickSeconBtn.bind(this),
      },
    };
    this.sortKey$ = new BehaviorSubject('SCHOOL_NAME');
    this.sortDirection$ = new BehaviorSubject<TSortDirections>(Direction.asc);
    this.batchActionsMode$ = of(true);
    this.batchActions = this._initializeBatchActions();
    this.columns = [
      {
        columnName: 'Name',
        columnOrder: 0,
        graphQlKey: 'SCHOOL_NAME',
      },
      {
        columnName: 'dbn',
        columnOrder: 1,
        graphQlKey: 'SCHOOL_DBN',
      },
      {
        columnName: 'permissions',
        columnOrder: 2,
        graphQlKey: 'SCHOOL_PERMISSION',
      },
    ];

    this.columnIndexMap = this.columns.reduce((mapping, col: any, i: number) => {
      mapping[col.graphQlKey] = i;
      return mapping;
    }, {});

    this.listConfig = {
      emptyTableMessage: 'No schools at this permission level',
      listType: 'USER_SCHOOL_PORTFOLIO',
      noDataMessage: 'No schools to display',
      sortableColumns: false,
      maximumVisibleRowsPerGroup: 5,
    };
  }

  public onBatchEditAccessLevel(destination): void {
    const idsToMove = this._hashIdsToMove(this.batchActionsSelectedIds$.value);
    const { groupData, rowsToMove } = this._getNewGroupings(this.groupingData$.value, idsToMove, destination);
    const newGroupings = this._moveRows(groupData, rowsToMove, destination);
    this.groupingData$.next(newGroupings);
    this.batchActions.reset();
  }

  private _hashIdsToMove(idsToMove) {
    return idsToMove.reduce((acc, id) => {
      acc[id] = true;
      return acc;
    }, {});
  }

  private _getNewGroupings(groupData, idsToMove, destination) {
    return groupData.reduce(
      (groupAcc, grouping: IGroupData) => {
        grouping.rowData = (grouping.rowData.reduce((rowAcc, row) => {
          const meta = JSON.parse(row[0].meta);
          const schoolId = meta.data; // DBN
          const found = idsToMove[schoolId];

          if (found) {
            if (grouping.key === destination) {
              rowAcc.push(([...row] as unknown) as IRowData);
            } else {
              const newRow = row.map(column => {
                if (column.meta === 'EDITABLE_DROPDOWN') {
                  column.data = this.dropdownHuman[destination];
                }
                return column;
              });

              groupAcc.rowsToMove.push([...newRow]);
            }
          } else {
            rowAcc.push(([...row] as unknown) as IRowData);
          }
          return rowAcc;
        }, []) as unknown) as IRowData[][];
        groupAcc.groupData.push({ ...grouping });
        return groupAcc;
      },
      { groupData: [], rowsToMove: [] },
    );
  }

  private _moveRows(groupData, rowsToMove, destination) {
    return groupData.map(group => {
      if (group.key === destination) {
        group.rowData.push(...rowsToMove);
      }
      return group;
    });
  }

  public onSingleAccessLevel($event: { selectedKey: string; row: IRowData[] }): void {
    const { selectedKey, row } = $event;
    const idsToMove = {};
    const meta = JSON.parse(row[0].meta);
    const schoolId = meta.data; // DBN

    idsToMove[schoolId] = true;

    const destination = Object.keys(this.dropdownHuman).filter(key => {
      return this.dropdownHuman[key] === selectedKey;
    })[0];

    const { groupData, rowsToMove } = this._getNewGroupings(this.groupingData$.value, idsToMove, destination);
    const newGroupings = this._moveRows(groupData, rowsToMove, destination);
    this.groupingData$.next(newGroupings);
  }

  public onUncheckAll(): void {
    // save pending checkboxes selections(empty []) in parent's prop
    // which will re-enable all permission cells' dropdowns
    this.batchActions.reset();
  }

  // BATCH ACTION PART
  public onBatchAction($event: IBatchData): void {
    // save pending checkboxes selections(non-empty array) in parent's prop
    // which will disable all permission cell's dropdowns
    let ids: string[];
    const { updateAll, data, level } = $event;
    switch (level) {
      case 'SECTION':
        ids = (data as IRowData[]).map((row: IRowData) => this._getSchoolIdFromRow(row));
        break;
      case 'ROW':
        ids = Array.of(this._getSchoolIdFromRow(data));
        break;
      default:
        ids = [];
    }
    this.batchActions.updateStudentIds({ updateAll, ids });
  }

  public onFocusedGroup($event: {
    groupData: IGroupData;
    sortKey: string;
    sortDirection: TSortDirections;
    groupIndx: number;
  }) {
    const { groupIndx } = $event;
    const newGroupings: IGroupData[] = [];
    this.groupingData$.value.forEach((grouping: IGroupData, i: number) => {
      const newGrouping = { ...grouping };
      if (i === groupIndx) {
        newGrouping.showAll = true;
      }
      newGroupings.push(newGrouping);
    });
    this.groupingData$.next(newGroupings);
  }

  // either create or update
  public onClickPrimaryBtn(): void {
    this.permissionsChanges.cluster_edit_all = false;
    this.permissionsChanges.cluster_view_all = false;
    this.groupingData$.value.forEach((grouping: IGroupData) => {
      this.permissionsChanges[grouping.key] = grouping.rowData.map(row => {
        const meta = JSON.parse(row[0].meta);
        const schoolId = meta.data; // DBN
        return schoolId;
      });
    });

    if (this.isEditAllUser(this.permissionsChanges)) {
      this.permissionsChanges = {
        cluster_edit_all: true,
        cluster_view_all: false,
      };
    } else if (this.isViewAllUser(this.permissionsChanges)) {
      this.permissionsChanges = {
        cluster_view_all: true,
        cluster_edit_all: false,
      };
    }

    const permissions = this.permissionsChanges;
    this.clickedPrimaryBtn.emit({ permissions });
  }

  // either back or cancel
  public onClickSeconBtn(): void {
    const currentGrouping = this.groupingData$.value.map((grouping: IGroupData) => {
      grouping.showAll = false;
      return grouping;
    });
    this.clickedSeconBtn.emit({ currentGrouping });
  }

  private _getSchoolIdFromRow(row): string {
    const stub = row[0];
    const metaData = JSON.parse(stub.meta);
    const schoolId = metaData.data;
    return schoolId;
  }

  private _initializeBatchActions() {
    let idHash = {};
    const batchActions = {
      updateStudentIds: ($event: { updateAll: boolean; ids: string[] }): void => {
        const { updateAll, ids } = $event;
        ids.forEach((id: string) => {
          if (idHash[id] && !updateAll) delete idHash[id];
          else idHash[id] = true;
          return id;
        });
        const idsToSend = Object.keys(idHash);
        this.batchActionsSelectedIds$.next(idsToSend);
        if (idsToSend.length) {
          this.dynamicComponentTrigger = false;
        } else {
          this.dynamicComponentTrigger = true;
        }
      },
      reset: (): void => {
        idHash = {};
        this.batchActionsSelectedIds$.next([]);
        this.dynamicComponentTrigger = true;
      },
      init: (): void => {
        this.batchActionsSelectedIds$ = new BehaviorSubject([]);
        this.dynamicComponentTrigger = true;
      },
    };
    this.batchActionsSelectedIds$ = new BehaviorSubject([]);
    this.dynamicComponentTrigger = true;
    return batchActions;
  }

  private isEditAllUser(permissions): boolean {
    let isEditAll = false;
    if (permissions.view_all.length === 0 && permissions.no_access.length === 0 && permissions.edit_all.length !== 0) {
      isEditAll = true;
    }
    return isEditAll;
  }

  private isViewAllUser(permissions): boolean {
    let isViewAll = false;
    if (permissions.edit_all.length === 0 && permissions.no_access.length === 0 && permissions.view_all.length !== 0) {
      isViewAll = true;
    }
    return isViewAll;
  }
}
